import * as React from "react";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as S from "../../../services";
import { T, TB, TC } from "../../../Constants";

export type AutoTicketsProps = {
    /** The id of the item linked to the ticket */
    item_id: string;
    /** The period of time to create the tickets in */
    period: Record<"from" | "to", number>;
    /** Display the component in a popup */
    popup?: boolean;
    /** Style to apply to the modal */
    modal?: M.StyleModalProps;
    /** Callback to handle the tickets that were created */
    on_create: (tickets: T.Submission<T.TicketData>[]) => void;
    /** Callback when cancelling the creation */
    on_cancel: () => void;
}

type Resources = Awaited<ReturnType<typeof S.getNenTicketResources>>["data"];
type Row = Parameters<T.API.Utils.Missions.CreateAutoTickets>[0]["rows"][number];

const get_cost = (row: Partial<Row>): number => {
    if (typeof row?.amount !== "number" || isNaN(row?.amount)) return undefined;
    if (typeof row?.unit_cost !== "number" || isNaN(row?.unit_cost)) return undefined;
    return row.amount * row.unit_cost;
}

const AutoTickets: React.FC<AutoTicketsProps> = ({ on_create, ...props }) => {
    const input_ref = React.useRef<C.QuickInputRef<Row>>(null);
    const default_period = React.useMemo(() => TB.getDate("01/01/" + props.period.from).toISOString(), [props.period.from]);
    const [rows, set_rows] = React.useState<Row[]>([{ type: "maintenance", date: default_period }]);
    const [resources, set_resources, status] = H.useAsyncState<Resources>({ ticket_types: [], actions: [], remarques: [], type: null, element: {}, equip_number_props: [] });

    React.useEffect(() => {
        let isSubscribed = true;
        S.getNenTicketResources(props.item_id)
            .then(r => isSubscribed && set_resources(r.data, "done"))
            .catch(() => isSubscribed && set_resources({ ticket_types: [], actions: [], remarques: [], type: null, element: {}, equip_number_props: [] }, "error"));
        return () => {
            isSubscribed = false;
            set_resources({ ticket_types: [], actions: [], remarques: [], type: null, element: {}, equip_number_props: [] }, "load");
        };
    }, [set_resources, props.item_id]);

    const get_actions = React.useMemo(() => ({
        // Don't show the actions already selected in other rows
        main: (row: Row) => {
            const used_actions = rows.map(r => r.action).filter(TB.mongoIdValidator);
            return resources.actions.filter(a => row.action === a.value || !used_actions.includes(a.value));
        },
        // Don't show the current action
        blocked: (row: Row) => {
            if (!row.action) return resources.actions;
            else return resources.actions.filter(a => a.value !== row.action);
        },
    }), [resources.actions, rows]);

    const columns = React.useMemo<C.QuickInputProps2<Row>["columns"]>(() => [
        { prop: "action", label: TC.TICKET_CREATOR_COL_ACTION, type: "select", typeahead: { dropdownFit: true }, getOptions: get_actions.main },
        { prop: "title", asterisk: true, label: TC.TICKET_CREATOR_COL_TITLE, type: "text" },
        { prop: "type", asterisk: true, label: TC.TICKET_CREATOR_COL_TYPE, type: "select", options: resources.ticket_types, typeahead: { dropdownFit: true }, no_clear_btn: true, init_value: "maintenance" as T.TicketData["type"] },
        { prop: "unit_cost", hidden: resources.type === "emp", label: TC.TICKET_CREATOR_COL_COST_UNIT, type: "number", suffix: TC.SUFFIX_EURO_PER_UNIT },
        { prop: "amount", hidden: resources.type === "emp", label: TC.TICKET_CREATOR_COL_UNIT, type: "number" },
        { prop: "amount_prop", hidden: resources.type === "emp", label: TC.TICKET_CREATOR_COL_UNIT_PROP, type: "select", options: resources.equip_number_props, typeahead: { dropdownFit: true } },
        { prop: "cost", asterisk: true, label: TC.TICKET_CREATOR_COL_COST, type: "number", suffix: "€" },
        { prop: "frequency", label: TC.TICKET_CREATOR_COL_PERIOD, type: "frequency", noText: true },
        { prop: "date", asterisk: true, label: TC.TICKET_CREATOR_COL_START_DATE, type: "date", init_value: default_period },
        { prop: "blocked", size: 2, label: TC.TICKET_CREATOR_COL_BLOCKED, type: "select", multiple: true, getOptions: get_actions.blocked, typeahead: { dropdownFit: true, height: "md" } },
        { prop: "remarque", label: TC.TICKET_CREATOR_COL_REM, type: "select", options: resources.remarques, typeahead: { dropdownFit: true } },
    ], [resources, get_actions]);

    const check_rows = React.useCallback(() => {
        let row_has_error = false;
        const errors: ReturnType<C.QuickInputProps2<Row>["onCheck"]> = [];

        for (const row of rows) {
            const start_date = TB.getDate(row.date);
            let row_error: typeof errors[number] = {};
            if (!row.type) row_error.type = TC.GLOBAL_REQUIRED_FIELD;
            if (!row.title) row_error.title = TC.GLOBAL_REQUIRED_FIELD;
            if (!start_date) row_error.date = TC.GLOBAL_REQUIRED_FIELD;
            // Check there is a cost, and not a negative one
            if (typeof row.cost !== "number" || isNaN(row.cost)) row_error.cost = TC.GLOBAL_REQUIRED_FIELD;
            else if (row.cost < 0) row_error.cost = TC.GLOBAL_NUMBER_MUST_BE_POSITIVE;
            // Check there is an amount, and not a negative one
            if (row.amount < 0) row_error.amount = TC.GLOBAL_NUMBER_MUST_BE_POSITIVE;
            // Check there is a unit cost, and not a negative one
            if (row.unit_cost < 0) row_error.unit_cost = TC.GLOBAL_NUMBER_MUST_BE_POSITIVE;

            // Flag that there is at least one error
            if (Object.keys(row_error).length > 0) row_has_error = true;
            errors.push(row_error);
        }

        if (row_has_error) input_ref.current?.set_errors?.(errors);
        else {
            const dismount = M.renderLoader();
            S.createAutoTickets({ rows, item: props.item_id, period: props.period })
                .then(tickets => on_create?.(tickets.data))
                .catch(M.Alerts.updateError)
                .finally(dismount);
        }
    }, [rows, props.item_id, props.period, on_create]);

    const footer = React.useMemo(() => <C.Flex justifyContent="end">
        <C.Button
            icon="plus"
            text={TC.TICKET_CREATOR_ADD_LINE}
            onClick={() => input_ref.current?.add?.()}
        />
        <C.Button
            variant="sector"
            className="ms-2"
            icon="hat-wizard"
            onClick={check_rows}
            text={TC.TICKET_CREATOR_MAGIC_BUTTON}
        />
    </C.Flex>, [check_rows]);

    const on_change = React.useCallback<C.QuickInputProps2<Row>["on_change"]>((value, row_index, value_prop) => {
        set_rows(rows => {
            const previous_row = rows[row_index];
            // The changes to apply to the row
            const changes: Partial<Row> = { [value_prop]: value };
            const action = resources.actions.find(a => a.value === value);

            if (!previous_row?.type) changes.type = "maintenance";
            // Use the default values of the action to populate the other fields
            if (value_prop === "action" && value) {
                changes.title = action?.label;
                changes.unit_cost = action?.cost;
                changes.amount_prop = action?.unit;
                changes.duration = action?.duration;
                changes.frequency = action?.frequency;
                changes.blocked = action?.stop_actions || [];
                changes.amount = resources.element[action?.unit];
                changes.cost = get_cost(changes);
            }
            else if (value_prop === "amount_prop") {
                changes.amount = resources.element[changes.amount_prop];
                changes.cost = get_cost({ ...previous_row, ...changes });
            }
            // Re-calculate the total amount of the ticket
            else if (value_prop === "amount" || value_prop === "unit_cost" /* || value_prop === "amount_prop" */) {
                changes.cost = get_cost({ ...previous_row, ...changes });
            }
            // The row_index is not in the current array, so we need to add it
            if (row_index >= rows.length) return rows.concat({ type: "maintenance", ...changes });
            else return rows.map((row, index) => index === row_index ? { ...row, ...changes } : row);
        });
    }, [resources.actions]);

    return React.createElement(
        props.popup ? M.BlankModal : React.Fragment,
        props.popup ? {
            ...props.modal,
            footer: footer,
            onQuit: props.on_cancel,
            size: props.modal?.size || "lg",
            title: props.modal?.title || TC.TICKET_CREATOR_TITLE,
            isFullScreen: typeof props.modal?.isFullScreen === "boolean" ? props.modal.isFullScreen : true,
        } as M.BlankModalProps : null,
        <C.Spinner status={status}>
            {/* The submit will always be called before the change */}
            <C.QuickInput2<Row>
                no_footer
                rows={rows}
                ref={input_ref}
                columns={columns}
                grid_width="120vw"
                onSubmit={set_rows}
                on_change={on_change}
                controlled="per_value"
            />
        </C.Spinner>
    );
}

export default AutoTickets;